import { ymdLocal } from '../lib/format.js';

// Staffing-coverage tier for a single period / a whole day (used only here).
function periodTierFor(period) {
  const req = Number(period.required_staff) || 0;
  const avail = Number(period.min_available) || 0;
  if (req <= 0) return 'green';
  const ratio = avail / req;
  if (ratio >= 1) return 'green';
  if (ratio >= 0.75) return 'yellow';
  if (ratio >= 0.5) return 'orange';
  return 'red';
}
function dayTierFromPeriods(day) {
  if (day.closed) return 'gray';
  if (!Array.isArray(day.periods) || day.periods.length === 0) return 'gray';
  const rank = { green: 0, yellow: 1, orange: 2, red: 3 };
  let worst = 'green';
  for (const p of day.periods) {
    const t = periodTierFor(p);
    if (rank[t] > rank[worst]) worst = t;
  }
  return worst;
}

export function QuietCalendar({ days, onSelect, tierColors, selectedYmd }) {
  const todayYmd = ymdLocal(new Date());
  // Group into full weeks starting Monday. Server returns Mon-anchored,
  // so we can slice 7 at a time.
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  const dowLabels = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 4, marginBottom: 4 }}>
        <div />
        {dowLabels.map((l) => (
          <div key={l} style={{ fontSize: 9, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: 0.4, textAlign: 'center' }}>{l}</div>
        ))}
      </div>
      {weeks.map((wk, wi) => {
        if (wk.length === 0) return null;
        const weekLabel = new Date(wk[0].ymd + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return (
          <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 4, marginBottom: 4 }}>
            <div style={{ fontSize: 9, color: '#9e9e9e', alignSelf: 'center', padding: '0 4px' }}>{weekLabel}</div>
            {Array.from({ length: 7 }).map((_, di) => {
              const d = wk[di];
              if (!d) return <div key={di} />;
              const tier = dayTierFromPeriods(d);
              const c = tierColors[tier] || tierColors.gray;
              const isToday = d.ymd === todayYmd;
              const isSel = d.ymd === selectedYmd;
              // Quiet if healthy (no bg, subtle border). Loud otherwise.
              const isHealthy = tier === 'green';
              const dayNum = new Date(d.ymd + 'T12:00:00Z').getUTCDate();
              const periodBadges = (d.periods || []).filter((p) =>
                (Number(p.required_staff) || 0) > (Number(p.min_available) || 0)
              );
              return (
                <button key={d.ymd}
                  onClick={() => onSelect(d)}
                  disabled={d.closed}
                  style={{
                    fontFamily: 'inherit',
                    background: isHealthy || d.closed ? '#fff' : c.bg,
                    border: `1px solid ${isSel ? '#1B2B1B' : isHealthy ? '#f0f0f0' : c.stripe}`,
                    borderRadius: 6,
                    padding: 6,
                    textAlign: 'left',
                    cursor: d.closed ? 'default' : 'pointer',
                    minHeight: 46,
                    opacity: d.closed ? 0.6 : 1,
                    position: 'relative',
                    outline: isSel ? '2px solid #1B2B1B' : 'none',
                    outlineOffset: -1,
                  }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span style={{
                      fontSize: 12,
                      fontWeight: isHealthy ? 400 : 700,
                      color: isHealthy ? '#616161' : c.stripe,
                    }}>{dayNum}</span>
                    {isToday && (
                      <span style={{ fontSize: 8, fontWeight: 700, background: '#1565c0', color: '#fff', padding: '0 4px', borderRadius: 999 }}>today</span>
                    )}
                  </div>
                  {d.closed ? (
                    <div style={{ fontSize: 9, color: '#9e9e9e', marginTop: 2 }}>closed</div>
                  ) : (
                    <>
                      {!isHealthy && periodBadges.length > 0 && (
                        <div style={{ display: 'flex', gap: 2, marginTop: 4 }}>
                          {periodBadges.slice(0, 4).map((p, i) => {
                            const pt = periodTierFor(p);
                            const pc = tierColors[pt] || tierColors.gray;
                            return (
                              <span key={i}
                                title={`${p.name}: ${p.min_available}/${p.required_staff}`}
                                style={{ flex: 1, height: 4, borderRadius: 2, background: pc.stripe, opacity: 0.9 }} />
                            );
                          })}
                        </div>
                      )}
                      {/* CJ 2026-07-29: show time-off + availability on every day so
                          clusters (9 off a Saturday) and thin days are visible even
                          when the tier holds. 🌴N = on time off; N/M = available at
                          the thinnest hour / need. */}
                      <div style={{ display: 'flex', gap: 5, marginTop: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                        {d.torCount > 0 && (
                          <span title={`${d.torCount} on time off`}
                            style={{ fontSize: 9, fontWeight: 700, color: '#8a5a1f', background: '#fff3e0', borderRadius: 3, padding: '0 3px' }}>🌴{d.torCount}</span>
                        )}
                        {d.minAvail != null && d.maxNeed != null && (
                          <span title={`${d.minAvail} available at the thinnest hour · need ${d.maxNeed}`}
                            style={{ fontSize: 9, fontWeight: 700, color: d.minAvail >= d.maxNeed ? '#2e7d32' : '#c62828' }}>
                            {d.minAvail}/{d.maxNeed}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
